import { useNotificationActions } from "@/features/Notification";
import { useDashboard } from "./useDashboard";
import { useErrorAlert } from "@/shared/lib/useErrorAlert";
import {
  CompanyPlanResultDto,
  CompanyResult,
  DepartmentsResultDto,
  DeparrtmentPlanResultDto,
  DepartmentPlanResultInterface,
  LtwCompanyResultItem,
  MyPlanResultDto,
  MyPlanResultInterface,
  TopDepartmentResultInterface,
  TopDepartmentsDto,
  TopDepartmentsResult,
  TopEmployeesDto,
  TopEmployeesResult,
  UserPlanResultDto,
} from "../model/dashboard.types";
import dashboardApi from "../api/dashboard.api";
import { handleApiResponse } from "@/shared/lib/api";
import { useCallback, useMemo } from "react";

const getErrorMessage = (error: unknown): string => {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  const responseMessage = (error as { response?: { data?: { error_message?: unknown } } })
    ?.response?.data?.error_message;
  return typeof responseMessage === "string" ? responseMessage : "Ошибка загрузки данных";
};

const useDashboardActions = () => {
  const {
    setDashboardTopDepartments,
    setDepartmentPlanResult,
    setUserPlanResult,
    setMyPlanResult,

    setLoadingState,
    setErrorState,
  } = useDashboard();
  const { showErrorAlert } = useErrorAlert();
  const { topAlert } = useNotificationActions();

  const getCompanyPlanResult = useCallback(async (
    data: CompanyPlanResultDto,
    options?: { skipStore?: boolean }
  ): Promise<CompanyResult | null> => {
    const skipStore = options?.skipStore === true;
    if (!skipStore) {
      setLoadingState("dashboardCompanyPlanResult", true);
      setErrorState("dashboardCompanyPlanResult", null);
    }
    try {
      const res = await dashboardApi.getCompanyPlanResult(data);
      const result = handleApiResponse<CompanyResult>(res, { showErrorAlert, topAlert });
      return result;
    } catch (error: any) {
      if (!skipStore) {
        setErrorState("dashboardCompanyPlanResult", getErrorMessage(error));
      }
      showErrorAlert(error);
      return null;
    } finally {
      if (!skipStore) {
        setLoadingState("dashboardCompanyPlanResult", false);
      }
    }
  }, [setLoadingState, setErrorState, showErrorAlert, topAlert]);

  const getTopEmployees = useCallback(async (
    data: TopEmployeesDto
  ): Promise<TopEmployeesResult[]> => {
    setLoadingState("dashboardTopEmployees", true);
    setErrorState("dashboardTopEmployees", null);
    try {
      const res = await dashboardApi.getTopEmployees(data);
      const result = handleApiResponse<TopEmployeesResult[]>(res, { showErrorAlert, topAlert }) ?? [];
      return result;
    } catch (error: any) {
      setErrorState("dashboardTopEmployees", getErrorMessage(error));
      showErrorAlert(error);
      return [];
    } finally {
      setLoadingState("dashboardTopEmployees", false);
    }
  }, [setLoadingState, setErrorState, showErrorAlert, topAlert]);

  const getTopDepartments = useCallback(async (data: TopDepartmentsDto): Promise<TopDepartmentResultInterface[]> => {
    setLoadingState("dashboardTopDepartments", true);
    setErrorState("dashboardTopDepartments", null);
    try {
      const res = await dashboardApi.getTopDepartments(data);
      const result = handleApiResponse<TopDepartmentResultInterface[]>(res, { showErrorAlert, topAlert }) ?? [];
      return result;
    } catch (error: any) {
      setErrorState("dashboardTopDepartments", getErrorMessage(error));
      showErrorAlert(error);
      return [];
    } finally {
      setLoadingState("dashboardTopDepartments", false);
    }
  }, [setLoadingState, setErrorState, showErrorAlert, topAlert]);

  const getLTWCompany = useCallback(async (data: DeparrtmentPlanResultDto): Promise<LtwCompanyResultItem[]> => {
    setLoadingState("dashboardLTWCompany", true);
    setErrorState("dashboardLTWCompany", null);
    try {
      const res = await dashboardApi.getLTWCompany(data);
      const result = handleApiResponse<LtwCompanyResultItem[]>(res, { showErrorAlert, topAlert }) ?? [];
      return result;
    } catch (error: any) {
      setErrorState("dashboardLTWCompany", getErrorMessage(error));
      showErrorAlert(error);
      return [];
    } finally {
      setLoadingState("dashboardLTWCompany", false);
    }
  }, [setLoadingState, setErrorState, showErrorAlert, topAlert]);

  const getMyPlanResult = useCallback(async (data: MyPlanResultDto): Promise<MyPlanResultInterface | null> => {
    setLoadingState("dashboardMyPlanResult", true);
    setErrorState("dashboardMyPlanResult", null);
    try {
      const res = await dashboardApi.getMyPlanResult(data);
      const result = handleApiResponse<MyPlanResultInterface | null>(res, { showErrorAlert, topAlert });
      if (result) setMyPlanResult(result);
      return result;
    } catch (error: any) {
      setErrorState("dashboardMyPlanResult", getErrorMessage(error));
      showErrorAlert(error);
      return null;
    } finally {
      setLoadingState("dashboardMyPlanResult", false);
    }
  }, [setLoadingState, setErrorState, setMyPlanResult, showErrorAlert, topAlert]);

  const getUserPlanResult = useCallback(async (
    data: UserPlanResultDto,
    options?: { skipStore?: boolean }
  ): Promise<MyPlanResultInterface | null> => {
    if (data.userId === 0) {
      return null
    }
    const skipStore = options?.skipStore === true;
    if (!skipStore) {
      setLoadingState("dashboardUserPlanResult", true);
      setErrorState("dashboardUserPlanResult", null);
    }
    try {
      const res = await dashboardApi.getUserPlanResult(data);
      const result = handleApiResponse<MyPlanResultInterface | null>(res, { showErrorAlert, topAlert });
      if (result && !skipStore) setUserPlanResult(result);
      return result;
    } catch (error: any) {
      if (!skipStore) {
        setErrorState("dashboardUserPlanResult", getErrorMessage(error));
      }
      showErrorAlert(error);
      return null;
    } finally {
      if (!skipStore) {
        setLoadingState("dashboardUserPlanResult", false);
      }
    }
  }, [setLoadingState, setErrorState, setUserPlanResult, showErrorAlert, topAlert]);

  const getDepartmentPlanResult = useCallback(async (
    data: DeparrtmentPlanResultDto,
    options?: { skipStore?: boolean }
  ): Promise<DepartmentPlanResultInterface | null> => {
    if (data.departmentId === 0) return null;
    const skipStore = options?.skipStore === true;
    if (!skipStore) {
      setLoadingState("dashboardDepartmentPlanResult", true);
      setErrorState("dashboardDepartmentPlanResult", null);
    }
    try {
      const res = await dashboardApi.getDepartmentPlanResult({
        dateStart: data.dateStart,
        dateEnd: data.dateEnd,
        isPeriod: data.isPeriod,
        departmentId: data.departmentId
      });
      const result = handleApiResponse<MyPlanResultInterface | null>(res, { showErrorAlert, topAlert });
      if (result && !skipStore) setDepartmentPlanResult(result);
      return result;
    } catch (error: any) {
      if (!skipStore) {
        setErrorState("dashboardDepartmentPlanResult", getErrorMessage(error));
      }
      showErrorAlert(error);
      return null;
    } finally {
      if (!skipStore) {
        setLoadingState("dashboardDepartmentPlanResult", false);
      }
    }
  }, [setLoadingState, setErrorState, setDepartmentPlanResult, showErrorAlert, topAlert]);

  const getDepartmentsResult = useCallback(async (data: DepartmentsResultDto): Promise<CompanyResult | null> => {
    try {
      const res = await dashboardApi.getDepartmentsResult(data);
      return handleApiResponse<CompanyResult>(res, { showErrorAlert, topAlert });
    } catch (error: any) {
      showErrorAlert(error);
      return null;
    }
  }, [showErrorAlert, topAlert]);

  const getDepartmentPlanByEmployees = useCallback(async (
    data: DeparrtmentPlanResultDto
  ): Promise<DepartmentPlanResultInterface | null> => {
    if (data.departmentId === 0) return null;
    try {
      const res = await dashboardApi.getDepartmentPlanByEmployees(data);
      return handleApiResponse<MyPlanResultInterface | null>(res, { showErrorAlert, topAlert });
    } catch (error: any) {
      showErrorAlert(error);
      return null;
    }
  }, [showErrorAlert, topAlert]);

  return useMemo(() => ({
    getDepartmentsResult,
    getDepartmentPlanResult,
    getDepartmentPlanByEmployees,
    getCompanyPlanResult,
    getUserPlanResult,
    getTopDepartments,
    getMyPlanResult,
    getTopEmployees,
    getLTWCompany,
  }), [
    getDepartmentsResult,
    getDepartmentPlanResult,
    getDepartmentPlanByEmployees,
    getCompanyPlanResult,
    getUserPlanResult,
    getTopDepartments,
    getMyPlanResult,
    getTopEmployees,
    getLTWCompany,
  ]);
};

export {useDashboardActions};
